import type { ChartHoverAnnotationRow } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import type { MetricType } from 'features/charts-v2/types'
import {
  formatMetricValue,
  getMetricLabel,
  VOLUME_LABEL_FOR,
} from 'features/charts-v2/utils/metric-format-utils'

/** Plain metric row — formatted value, no percentage. */
export function metricRow(
  metric: MetricType,
  value: number,
  color: string | null = null,
): ChartHoverAnnotationRow {
  return {
    label: getMetricLabel(metric),
    value: formatMetricValue(value, metric),
    percentage: null,
    color,
  }
}

/**
 * Companion volume row for sole rating/sentiment charts.
 * Returns null when the chart is not a sole rating/sentiment chart,
 * or when volumeTotal <= 0.
 *
 * Caller passes the datapoint's volumeCount (values[1]) and
 * the sum of volumeCount across all visible datapoints (volumeTotal).
 */
export function companionVolumeRow(
  primaryMetric: MetricType,
  volumeCount: number | null | undefined,
  volumeTotal: number,
): ChartHoverAnnotationRow | null {
  const label = VOLUME_LABEL_FOR[primaryMetric]
  if (!label || volumeCount == null || volumeTotal <= 0) return null
  const share = (volumeCount / volumeTotal) * 100
  return {
    label,
    value: volumeCount.toLocaleString(),
    percentage: `${share.toFixed(1)}%`,
    color: null,
  }
}

/**
 * Count-metric row (record_count / sentence_count) with parenthetical
 * share when a total is available.
 */
export function countMetricRow(
  metric: MetricType,
  value: number,
  total: number,
  color: string | null = null,
): ChartHoverAnnotationRow {
  const pct = total > 0 ? ` (${((value / total) * 100).toFixed(1)}%)` : ''
  return {
    label: getMetricLabel(metric),
    value: `${value.toLocaleString()}${pct}`,
    percentage: null,
    color,
  }
}

/** Pre-formatted category row (stacked bar/area slices, pie slices). */
export function categoryRow(
  label: string,
  formattedValue: string,
  formattedPercentage: string | null,
  color: string | null,
): ChartHoverAnnotationRow {
  return {
    label,
    value: formattedValue,
    percentage: formattedPercentage,
    color,
  }
}
